import React, {useRef, useState} from 'react'
import {InputFieldSet} from "../../components/common/InputFieldSet";
import SpinnerButton from "../../components/common/SpinnerButton";
import {apiConfig} from "../../api/ApiConfig";
import {encrypt, validPasswordFormat} from "../../utils/AppUtils";
import VerifyMFAModal from "./VerifyMFAModal";
import {useNavigate} from "react-router-dom";
import {SetupMfa} from "./SetupMfa";

const {getUserByEmail, getUserByUserId, createUser, getPublicKey, confirmSignUp} = apiConfig();

export const SignUp = () => {

    const [user, setUser] = useState({});
    const [statusMsg, setStatusMsg] = useState("");
    const [formErrors, setFormErrors] = useState({});
    const [mfa, setMfa] = useState({mfaEnabled: false, qrCodeUrl: null});
    const navigate = useNavigate();
    const mfaModalRef = useRef()

    const update = (e) => {
        setStatusMsg("");
        const {name, value} = e;
        if (value !== null || value !== '') {
            setError([name], null)
        }
        setUser(preState => ({
            ...preState,
            [name]: value
        }))
    }

    const validate = (e) => {
        const {name, value} = e;

        //Validate userId
        if (name === 'userId' && value && value !== '') {
            isExistingUser(name, value);
        }

        //Validate email address format and if already registered
        if (name === 'email' && value && value !== '') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                setError(name, "Please enter a valid email address.");
            } else if (!isEmailRegistered(name, value)) {
                setError(name, null);
            }
        }

        // Validate Password
        if (name === 'password' && value) {
            setError(name, validPasswordFormat(value))
        }

        if (name === 'confirmPassword') {
            if (user.confirmPassword && user.password && user.confirmPassword !== user.password) {
                setError(name, "Passwords are not same.")
            } else {
                setError(name, null)
            }
        }
    }

    const isExistingUser = async (name, userId) => {
        try {
            const data = await getUserByUserId(userId);
            if (data) {
                setError(name, "UserId in use.");
                return true;
            }
        } catch (error) {
            setError(name, "Error in finding User.");
            return true;
        }
        setError(name, null)
        return false;
    };

    const setError = (name, value) => {
        const msg = {[name]: value}
        if (!value) {
            setFormErrors(prevState => {
                const newState = {...prevState};
                delete newState[name];
                return newState;
            });

        } else {
            setFormErrors(preState => ({
                ...preState,
                ...msg
            }))
        }
    }

    const isEmailRegistered = async (name, email) => {
        try {
            const data = await getUserByEmail(email)
            if (data) {
                setError(name, "Registered Email. Please login")
                return true;
            }
        } catch (error) {
            setError(name, "Error in finding Email.")
            return true;
        }
        setError({name: null})
        return false;
    };

    const validateMandatoryFields = () => {
        if (!user.name) {
            setError('name', 'Name is mandatory')
        }
        if (!user.userId) {
            setError('userId', 'UserId is mandatory')
        }
        if (!user.password) {
            setError('password', 'Password is mandatory')
        }
        if (user.password && !user.confirmPassword) {
            setError('confirmPassword', 'Confirm Password')
        }
        if (!user.email) {
            setError('email', 'Email is mandatory')
        }
    }

    const handleSubmit = async (e) => {
        validateMandatoryFields();
        if (Object.keys(formErrors).length !== 0) {
            return false
        }
        try {
            const {password, confirmPassword, ...userInfo} = user;
            const publicKey = await getPublicKey(userInfo.userId);
            console.log(publicKey);

            const encPass = btoa(await encrypt(password, publicKey))
            console.log(encPass);

            const userPayload = {...userInfo, 'password': encPass};
            console.log(userPayload);
            const response = await createUser(JSON.stringify(userPayload));
            if (response.ok) {
                setStatusMsg('User created successfully! A link has been sent to your email Id to activation your account, before Login!')
            } else {
                setStatusMsg(`Failed to create user. Status: ${response.status}`)
            }
            const userResponse = await response.json()
            setMfa({mfaEnabled: userResponse.mfaEnabled, qrCodeUrl: userResponse.qrCodeUrl})
        } catch (error) {
            console.error('Error occurred while creating user:', error)
            setStatusMsg('Failed to create user. Check logs for details.')
        }
        return false
    }

    const afterSignUpNavigateTo = (navigateTo) => {
        if (window.location.pathname === navigateTo) {
            window.location.reload();
        } else {
            navigate(navigateTo);
        }
        mfaModalRef.current.close();
    }

    const handleClickVerify = () => {
        mfaModalRef.current.open();
    }

    const verifyOtp = async (otp) => {
        const response = await confirmSignUp(otp, user.userId, true)
        console.log(`Verify OTP response - ${response}`)
        return {success: response, nextStep: () => afterSignUpNavigateTo('/login')}
    }

    return (
        <>
            <VerifyMFAModal handleVerifyOtp={verifyOtp} ref={mfaModalRef}/>
            {!mfa.qrCodeUrl ?
                <div>
                    <div className="success">{statusMsg}</div>
                    <InputFieldSet
                        name="name"
                        useName={true}
                        mandatory={true}
                        label={'Full Name'}
                        onChange={update}
                        onBlur={validate}
                        error={formErrors.name}
                    />
                    <InputFieldSet
                        name="userId"
                        useName={true}
                        mandatory={true}
                        label={'UserId'}
                        onChange={update}
                        onBlur={validate}
                        error={formErrors.userId}
                    />
                    <InputFieldSet
                        name="email"
                        useName={true}
                        mandatory={true}
                        label={'Email'}
                        onChange={update}
                        type="email"
                        onBlur={validate}
                        error={formErrors.email}
                    />
                    <InputFieldSet
                        name="password"
                        useName={true}
                        mandatory={true}
                        label={'Password'}
                        onChange={update}
                        type="password"
                        onBlur={validate}
                        error={formErrors.password}
                    />
                    <InputFieldSet
                        name="confirmPassword"
                        useName={true}
                        mandatory={true}
                        label={'Confirm Password'}
                        onChange={update}
                        type="password"
                        onBlur={validate}
                        error={formErrors.confirmPassword}
                    />
                    <SpinnerButton
                        label="Sign Up"
                        className="btn-block"
                        onClick={handleSubmit}
                        buttonColor={"#001645"}
                    />
                </div>
                :
                <SetupMfa mfa={mfa} handleClick={handleClickVerify} title="Setup Two-Factor Authentication" />
            }
        </>
    )
}
